import React from "react";
import { motion } from "framer-motion";
import telemedicine from "../../../assets/telemedicine.png";
import Navbar from "../../../DashBoardComponents/Navbar";
import {
  Video,
  CalendarCheck,
  FileSignature,
  FolderHeart,
  Paperclip,
  BellRing,
  Check,
  Building2,
  Stethoscope,
  UserRound,
  Microscope,
  Network,
  HomeIcon,
  ShieldCheck,
  Sparkles,
  ArrowRight,
  PhoneCall,
  ClipboardList,
  FileText,
  RefreshCcw,
  Lock,
  Search, // ADDED: needed for the new search bar in the hero
} from "lucide-react";
import { Link } from "react-router-dom";

// ============================================================
// ANIMATION VARIANTS
// ============================================================

const fadeUp = {
  hidden: { opacity: 0, y: 35 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: "easeOut" },
  },
};

const fadeLeft = {
  hidden: { opacity: 0, x: -45 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.8, ease: "easeOut" },
  },
};

const fadeRight = {
  hidden: { opacity: 0, x: 45 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.8, ease: "easeOut" },
  },
};

const stagger = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.12 },
  },
};

const viewport = {
  once: true,
  amount: 0.18,
};

export default function Telemedicine() {
  const features = [
    {
      icon: Video,
      title: "Virtual Consultations",
      desc: "Connect patients and healthcare professionals remotely when a virtual consultation is appropriate.",
    },
    {
      icon: CalendarCheck,
      title: "Appointments",
      desc: "Support appointment booking and consultation scheduling as part of the connected care journey.",
    },
    {
      icon: FileSignature,
      title: "e-Prescriptions",
      desc: "Support digital prescription workflows when clinically appropriate and available through the care workflow.",
    },
    {
      icon: FolderHeart,
      title: "Health Records",
      desc: "Keep relevant patient information connected to the consultation and ongoing care journey.",
    },
    {
      icon: Paperclip,
      title: "Medical Reports",
      desc: "Share relevant reports and documents with healthcare professionals during the care workflow.",
    },
    {
      icon: BellRing,
      title: "Follow-up Care",
      desc: "Support follow-up consultations and ongoing care after the initial virtual interaction.",
    },
  ];

  const benefitsLeft = [
    "Connect with healthcare professionals remotely when appropriate",
    "Reduce unnecessary travel for suitable consultations and follow-ups",
    "Access relevant digital healthcare information through the care workflow",
    "Keep prescriptions and reports connected to the patient's care journey",
  ];

  const benefitsRight = [
    "Support virtual consultations as part of the existing care workflow",
    "Connect appointments, patient information and prescriptions",
    "Support more organised digital consultation workflows",
    "Keep relevant care information available to authorised users",
  ];

  const howItWorks = [
    {
      icon: UserRound,
      step: "01",
      title: "Find Care",
      desc: "Find a healthcare professional and choose an appropriate consultation option.",
    },
    {
      icon: CalendarCheck,
      step: "02",
      title: "Connect",
      desc: "Choose an available appointment and connect with your healthcare professional remotely.",
    },
    {
      icon: Video,
      step: "03",
      title: "Consult",
      desc: "Discuss your health concerns through a virtual consultation.",
    },
    {
      icon: ClipboardList,
      step: "04",
      title: "Manage",
      desc: "Relevant patient information, prescriptions and care activities can remain connected to the workflow.",
    },
    {
      icon: RefreshCcw,
      step: "05",
      title: "Continue Care",
      desc: "Support follow-ups and ongoing care when a virtual interaction is appropriate.",
    },
  ];

  const patientBenefits = [
    "Connect with healthcare professionals remotely",
    "Reduce unnecessary travel for suitable consultations",
    "Book appointments through a digital workflow",
    "Access digital prescriptions when available",
    "Stay connected for follow-up care",
  ];

  const users = [
    { icon: Building2, label: "Hospitals" },
    { icon: Stethoscope, label: "Clinics" },
    { icon: UserRound, label: "Doctors" },
    { icon: Microscope, label: "Diagnostic Centers" },
    { icon: Network, label: "Healthcare Networks" },
    { icon: HomeIcon, label: "Home Healthcare" },
  ];

  const faqs = [
    {
      q: "What is telemedicine?",
      a: "Virtual care allows patients and healthcare professionals to connect remotely using digital technologies for appropriate healthcare interactions and services.",
    },
    {
      q: "What is a virtual consultation?",
      a: "A virtual consultation is a remote consultation between a patient and a healthcare professional using digital communication technology.",
    },
    {
      q: "Is telemedicine the same as a virtual consultation?",
      a: "Not exactly. Telemedicine refers to healthcare delivered remotely through digital technologies, while a virtual consultation is a specific remote interaction between a patient and a healthcare professional.",
    },
    {
      q: "How does a Doxzon virtual consultation work?",
      a: "Patients can find a healthcare professional, select an appropriate consultation option and connect remotely for their consultation through Doxzon.",
    },
    {
      q: "Can I receive a prescription after a virtual consultation?",
      a: "Where clinically appropriate and supported by the healthcare professional and applicable requirements, a prescription may be provided through the digital care workflow.",
    },
    {
      q: "Can my healthcare professional access my health records?",
      a: "Authorised healthcare professionals may access relevant patient information according to the permissions, workflows and access controls implemented by Doxzon.",
    },
    {
      q: "When should I visit a clinic instead of using telemedicine?",
      a: "Virtual care may be appropriate for some consultations and follow-ups, but it is not suitable for every healthcare situation. If a physical examination, urgent assessment or in-person procedure is needed, an in-person healthcare visit may be more appropriate.",
    },
  ];

  // ADDED: chip list for the new search-bar area in the hero (mirrors the
  // "Popular:" specialty chips in the reference screenshot)
  const popularSpecialties = [
    "General Physician",
    "Dermatologist",
    "Pediatrician",
    "Gynecologist",
  ];

  return (
    <>
      <Navbar />

      <div className="font-jakarta bg-white text-slate-900 overflow-hidden">
        {/* =====================================================
            HERO — redesigned to follow the reference screenshot's
            layout pattern: eyebrow pill, big headline, search bar,
            specialty chips, trust row, image with floating cards.
        ====================================================== */}
        <section className="relative min-h-[720px] bg-gradient-to-br from-cyan-50 via-white to-blue-50 overflow-hidden">
          {/* Animated background */}
          <motion.div
            className="absolute -top-40 -right-40 w-[620px] h-[620px] rounded-full bg-teal-300/20 blur-3xl"
            animate={{
              scale: [1, 1.15, 1],
              x: [0, -25, 0],
              y: [0, 25, 0],
            }}
            transition={{
              duration: 9,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />

          <motion.div
            className="absolute -bottom-48 -left-40 w-[550px] h-[550px] rounded-full bg-cyan-300/20 blur-3xl"
            animate={{
              scale: [1, 1.2, 1],
              x: [0, 30, 0],
            }}
            transition={{
              duration: 11,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />

          {/* Grid */}
          <div
            className="absolute inset-0 opacity-[0.035]"
            style={{
              backgroundImage:
                "linear-gradient(#0f172a 1px, transparent 1px), linear-gradient(90deg, #0f172a 1px, transparent 1px)",
              backgroundSize: "45px 45px",
            }}
          />

          <div className="relative max-w-7xl mx-auto px-6 py-24 lg:py-28 grid lg:grid-cols-2 gap-14 items-center">
            {/* LEFT */}
            <motion.div
              variants={fadeLeft}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
            >
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="inline-flex items-center gap-2 rounded-full border border-teal-200 bg-white/80 backdrop-blur px-4 py-2"
              >
                <motion.span
                  className="w-2 h-2 rounded-full bg-teal-500"
                  animate={{
                    scale: [1, 1.5, 1],
                    opacity: [1, 0.5, 1],
                  }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                />

                <span className="text-xs font-bold uppercase tracking-[0.2em] text-teal-600">
                  Telemedicine
                </span>
              </motion.div>

              <h1 className="font-sora text-5xl md:text-6xl lg:text-[58px] font-medium tracking-[-0.05em] leading-[1.05] mt-7">
                Your Complete
                <span className="relative block text-teal-500 mt-2">
                  Telemedicine Experience
                  <motion.span
                    className="absolute left-0 bottom-[-8px] h-[4px] rounded-full bg-cyan-400"
                    initial={{ width: 0 }}
                    animate={{ width: "62%" }}
                    transition={{ delay: 0.8, duration: 0.8 }}
                  />
                </span>
              </h1>

              <p className="text-slate-600 text-lg leading-8 mt-8 max-w-xl">
                Doxzon lets your clinicians see patients over video, write and
                share prescriptions the same way they would in person, and pull
                up a patient's history mid-call instead of asking them to
                repeat it.
              </p>

              {/* Search bar — adapted from the screenshot's "Search / City / Find a doctor"
                  bar to fit the telemedicine flow (specialty search + preferred date + slot CTA).
                  NOTE: these inputs are presentational only — no state/handlers wired up,
                  since this file had none before and I'm not inventing form logic. */}
              <div className="mt-9 flex flex-col sm:flex-row items-stretch gap-2 bg-white rounded-2xl border border-slate-100 shadow-lg shadow-slate-200/60 p-2">
                <div className="flex items-center gap-2 flex-1 px-3">
                  <Search size={18} className="text-slate-400 flex-shrink-0" />
                  <input
                    type="text"
                    placeholder="Search doctor or specialty"
                    className="w-full py-3 text-sm text-slate-700 outline-none placeholder:text-slate-400 bg-transparent"
                  />
                </div>

                <div className="hidden sm:block w-px bg-slate-100" />

                <div className="flex items-center gap-2 flex-1 px-3">
                  <CalendarCheck size={18} className="text-slate-400 flex-shrink-0" />
                  <input
                    type="text"
                    placeholder="Preferred date"
                    className="w-full py-3 text-sm text-slate-700 outline-none placeholder:text-slate-400 bg-transparent"
                  />
                </div>
                <Link to="/consultdoctor">
                  <motion.button
                    whileHover={{
                      scale: 1.03,
                      boxShadow: "0 15px 35px rgba(20,184,166,0.25)",
                    }}
                    whileTap={{ scale: 0.97 }}
                    className="inline-flex items-center justify-center gap-2 bg-slate-950 text-white px-6 py-3.5 rounded-xl font-semibold text-sm whitespace-nowrap"
                  >
                    Find a Slot
                    <ArrowRight size={16} />
                  </motion.button>
                </Link>
              </div>

              {/* Popular specialty chips */}
              <div className="flex flex-wrap items-center gap-2 mt-4">
                <span className="text-xs text-slate-400 mr-1">Popular:</span>
                {popularSpecialties.map((label) => (
                  <button
                    key={label}
                    type="button"
                    className="text-xs font-medium text-slate-600 bg-slate-50 border border-slate-200 rounded-full px-3 py-1.5 hover:border-teal-300 hover:text-teal-600 transition"
                  >
                    {label}
                  </button>
                ))}
              </div>

              <div className="flex flex-wrap gap-6 mt-8 text-sm text-slate-500">
                <span className="flex items-center gap-2">
                  <ShieldCheck size={17} className="text-teal-500" />
                  Secure consultations
                </span>

                <span className="flex items-center gap-2">
                  <Check size={17} className="text-teal-500" />
                  Digital prescriptions
                </span>

                {/*
                  Intentionally NOT adding the screenshot's "HIPAA Compliant"
                  badge or "4.9/5 from 28k reviews" line here — I have no
                  source in this project confirming Doxzon's actual
                  certification status or review numbers, so I'm not
                  fabricating trust claims into the code. Swap in real
                  copy here if/when you have verified figures.
                */}
              </div>
            </motion.div>

            {/* RIGHT VISUAL */}
            <motion.div
              variants={fadeRight}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              className="relative"
            >
              {/* Rotating rings */}
              <motion.div
                className="absolute inset-[-35px] rounded-[40px] border border-teal-200/60"
                animate={{ rotate: 360 }}
                transition={{
                  duration: 25,
                  repeat: Infinity,
                  ease: "linear",
                }}
              />

              <motion.div
                className="absolute inset-[-60px] rounded-[50px] border border-cyan-200/40"
                animate={{ rotate: -360 }}
                transition={{
                  duration: 35,
                  repeat: Infinity,
                  ease: "linear",
                }}
              />

              {/* Main image */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1, delay: 0.2 }}
                whileHover={{ scale: 1.015 }}
                className="relative z-10 mx-auto rounded-[30px] overflow-hidden border-[8px] border-white shadow-[0_25px_70px_rgba(15,23,42,0.15)]"
              >
                <img
                  src={telemedicine}
                  alt="Doctor providing a telemedicine virtual consultation"
                  className="w-full h-[370px] sm:h-[400px] lg:h-[430px] object-cover"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/45 via-transparent to-transparent" />

                {/* Live status */}
                <div className="absolute bottom-5 left-5 right-5">
                  <div className="bg-white/90 backdrop-blur-md rounded-2xl px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-teal-100 flex items-center justify-center">
                        <Video size={17} className="text-teal-600" />
                      </div>

                      <div>
                        <p className="text-[10px] text-slate-400">
                          Consultation
                        </p>
                        <p className="text-sm font-semibold">
                          Live consultation
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 text-xs font-semibold text-green-600">
                      <motion.span
                        className="w-2 h-2 bg-green-500 rounded-full"
                        animate={{ scale: [1, 1.5, 1] }}
                        transition={{ duration: 1.2, repeat: Infinity }}
                      />
                      LIVE
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Floating card 1 */}
              <motion.div
                className="absolute left-0 lg:left-2 top-10 z-20 bg-white rounded-xl shadow-xl border border-slate-100 p-2.5 w-40"
                animate={{ y: [0, -10, 0] }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center">
                    <Stethoscope size={16} className="text-teal-600" />
                  </div>

                  <div>
                    <p className="text-[9px] text-slate-400">Doctor</p>
                    <p className="text-xs font-bold">Available now</p>
                  </div>
                </div>

                <div className="mt-2 flex items-center gap-1.5 text-[10px] text-green-600 font-semibold">
                  <Check size={11} />
                  Ready for consultation
                </div>
              </motion.div>

              {/* Floating card 2 */}
              <motion.div
                className="absolute right-0 lg:right-2 bottom-16 z-20 bg-white rounded-xl shadow-xl border border-slate-100 p-2.5 w-44"
                animate={{ y: [0, 10, 0] }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-lg bg-cyan-50 flex items-center justify-center">
                    <FileSignature size={16} className="text-cyan-600" />
                  </div>

                  <div>
                    <p className="text-[9px] text-slate-400">
                      Prescription
                    </p>
                    <p className="text-xs font-bold">
                      Sent successfully
                    </p>
                  </div>
                </div>

                <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-teal-500 to-cyan-500 rounded-full"
                    initial={{ width: "0%" }}
                    animate={{ width: "100%" }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      repeatDelay: 3,
                    }}
                  />
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* =====================================================
            STATS
        ====================================================== */}
        <motion.section
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="max-w-6xl mx-auto px-6 -mt-10 relative z-20"
        >
          <div className="bg-white rounded-2xl border border-slate-100 shadow-xl p-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                ["01", "Find Care"],
                ["02", "Connect"],
                ["03", "Manage"],
                ["04", "Continue Care"],
              ].map(([value, label]) => (
                <div key={label} className="text-center">
                  <p className="font-sora text-2xl font-bold text-slate-950">
                    {value}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">{label}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* =====================================================
            INTRO
        ====================================================== */}
        <section className="py-28">
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
            className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center"
          >
            <motion.div variants={fadeLeft}>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-teal-500">
                The connected experience
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium tracking-[-0.04em] leading-tight mt-4">
                The consultation room
                <span className="text-teal-500"> is now everywhere.</span>
              </h2>
            </motion.div>

            <motion.div variants={fadeRight}>
              <p className="text-lg text-slate-600 leading-8">
                Patients can book an appointment, join a video consultation,
                share medical reports, communicate with doctors and receive
                prescriptions without travelling to the clinic.
              </p>

              <p className="text-lg text-slate-600 leading-8 mt-5">
                Doxzon brings these activities together into one connected
                healthcare workflow.
              </p>
            </motion.div>
          </motion.div>
        </section>

        {/* =====================================================
            FEATURES
        ====================================================== */}
        <section className="relative bg-[#f6fbfc] py-28 overflow-hidden">
          <motion.div
            className="absolute -right-40 top-20 w-[500px] h-[500px] rounded-full bg-teal-200/20 blur-3xl"
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 8, repeat: Infinity }}
          />

          <div className="relative max-w-7xl mx-auto px-6">
            <div className="max-w-2xl">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-teal-500">
                Capabilities
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium tracking-[-0.04em] mt-4">
                Everything needed for
                <span className="text-teal-500"> remote care.</span>
              </h2>
            </div>

            <motion.div
              variants={stagger}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.15 }}
              className="grid grid-flow-col-3 md:grid-cols-2 grid-cols-3 lg:grid-cols-3 gap-4 mt-10"
            >
              {features.map(({ icon: Icon, title, desc }, index) => (
                <motion.div
                  key={title}
                  variants={fadeUp}
                  whileHover={{ y: -8 }}
                  className="group relative bg-white rounded-2xl border border-slate-100 p-5 shadow-sm hover:shadow-2xl transition-shadow duration-300"
                >
                  <motion.div
                    whileHover={{ rotate: 8, scale: 1.08 }}
                    className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center group-hover:bg-teal-500 transition-colors"
                  >
                    <Icon
                      size={18}
                      className="text-teal-600 group-hover:text-white transition-colors"
                    />
                  </motion.div>

                  <span className="absolute top-5 right-5 font-mono text-[10px] text-slate-200 group-hover:text-teal-300 transition">
                    0{index + 1}
                  </span>

                  <h3 className="font-sora text-base font-semibold mt-5">
                    {title}
                  </h3>

                  <p className="text-slate-500 text-xs leading-6 mt-2">
                    {desc}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* =====================================================
            HOW IT WORKS
        ====================================================== */}
        <section className="py-28">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-teal-500">
                How it works
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">
                Five steps.
                <span className="text-teal-500">
                  {" "}One connected experience.
                </span>
              </h2>
            </div>

            <div className="relative grid grid-cols-5 sm:grid-cols-1 gap-8 md:gap-4 mt-16">
              <motion.div className="hidden md:block absolute top-8 left-[10%] right-[10%] h-[2px] bg-slate-100 overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-teal-500 to-cyan-500"
                  initial={{ width: "0%" }}
                  whileInView={{ width: "100%" }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.8, ease: "easeInOut" }}
                />
              </motion.div>

              {howItWorks.map(({ icon: Icon, step, title, desc }) => (
                <motion.div
                  key={step}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={viewport}
                  whileHover={{ y: -7 }}
                  className="relative text-center"
                >
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    className="relative z-10 mx-auto w-16 h-16 rounded-2xl bg-slate-950 border-4 border-white shadow-lg flex items-center justify-center"
                  >
                    <Icon size={23} className="text-teal-400" />
                  </motion.div>

                  <span className="block text-xs font-mono text-teal-500 tracking-widest mt-5">
                    STEP {step}
                  </span>

                  <h3 className="font-sora text-xl font-bold mt-2">
                    {title}
                  </h3>

                  <p className="text-slate-500 text-sm leading-7 mt-3 max-w-sm mx-auto">
                    {desc}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* =====================================================
            BENEFITS
        ====================================================== */}
        <section className="relative overflow-hidden bg-slate-950 py-28 text-white">
          <motion.div
            className="absolute left-0 right-0 top-1/2 h-px bg-teal-500/20"
            animate={{ opacity: [0.2, 0.7, 0.2] }}
            transition={{ duration: 2, repeat: Infinity }}
          />

          <div className="relative max-w-6xl mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <motion.div
                variants={fadeLeft}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-teal-400">
                  Why Doxzon
                </span>

                <h2 className="font-sora text-4xl md:text-5xl font-medium leading-tight mt-5">
                  Better access.
                  <br />
                  Better continuity.
                </h2>

                <p className="text-slate-400 text-lg leading-8 mt-6 max-w-lg">
                  Telemedicine should feel like an extension of your
                  healthcare system — not a separate product.
                </p>
              </motion.div>

              <motion.div
                variants={stagger}
                initial="hidden"
                whileInView="visible"
                viewport={viewport}
                className="grid sm:grid-cols-2 gap-4"
              >
                {[...benefitsLeft, ...benefitsRight].map((benefit) => (
                  <motion.div
                    key={benefit}
                    variants={fadeUp}
                    whileHover={{
                      scale: 1.03,
                      backgroundColor: "rgba(255,255,255,0.08)",
                    }}
                    className="flex items-start gap-2.5 bg-white/5 border border-white/10 rounded-xl p-4"
                  >
                    <div className="w-6 h-6 rounded-full bg-teal-500/15 flex items-center justify-center flex-shrink-0">
                      <Check size={13} className="text-teal-400" />
                    </div>

                    <span className="text-xs text-slate-300 leading-5">
                      {benefit}
                    </span>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>

        {/* =====================================================
            FOR HEALTHCARE PROVIDERS
        ====================================================== */}
        <section className="bg-slate-50 py-24">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
            className="max-w-6xl mx-auto px-6 grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center"
          >
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-teal-500">
                For healthcare professionals
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium tracking-tight mt-4">
                Deliver Care Beyond the Clinic
              </h2>

              <p className="text-gray-600 mt-6 leading-8">
                Telemedicine works best when it fits into the existing
                healthcare workflow rather than becoming another disconnected
                application. Doxzon connects virtual consultations with
                appointments, patient records, prescriptions, and other
                healthcare services.
              </p>
            </div>

            <motion.div
              variants={stagger}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              className="grid grid-cols-2 gap-4"
            >
              {[
                { icon: CalendarCheck, label: "Appointments" },
                { icon: FolderHeart, label: "Patient Records" },
                { icon: FileSignature, label: "Prescriptions" },
                { icon: Network, label: "Other Services" },
              ].map(({ icon: Icon, label }) => (
                <motion.div
                  key={label}
                  variants={fadeUp}
                  whileHover={{ y: -7, scale: 1.02 }}
                  className="bg-white border border-slate-100 rounded-xl p-4 text-center shadow-sm hover:shadow-lg transition"
                >
                  <motion.div
                    whileHover={{ rotate: 8, scale: 1.08 }}
                    className="w-9 h-9 mx-auto rounded-lg bg-gradient-to-br from-teal-500 to-cyan-500 grid place-items-center"
                  >
                    <Icon size={16} className="text-white" />
                  </motion.div>

                  <p className="text-xs font-semibold text-slate-800 mt-2">
                    {label}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </section>

        {/* =====================================================
            FOR PATIENTS
        ====================================================== */}
        <section className="py-24 bg-gradient-to-r from-cyan-50 to-blue-50 relative overflow-hidden">
          <motion.div
            className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-teal-500/10 blur-3xl"
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 7, repeat: Infinity }}
          />

          <div className="relative max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              variants={fadeLeft}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
            >
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-teal-500">
                For patients
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium tracking-tight mt-4">
                Healthcare That Fits Around Your Life
              </h2>

              <p className="text-gray-600 mt-6 leading-8">
                Virtual consultations can make healthcare more convenient for
                patients who may have difficulty travelling, need a follow-up
                consultation, or simply want to connect with their healthcare
                provider remotely.
              </p>
            </motion.div>

            <motion.ul
              variants={stagger}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              className="space-y-3"
            >
              {patientBenefits.map((item) => (
                <motion.li
                  key={item}
                  variants={fadeUp}
                  whileHover={{ x: 6 }}
                  className="flex items-center gap-3 bg-white/80 backdrop-blur border border-white rounded-lg p-3 shadow-sm"
                >
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-teal-500/10 text-teal-600 grid place-items-center">
                    <Check size={14} strokeWidth={2.5} />
                  </span>

                  <span className="text-gray-700 font-medium text-sm">{item}</span>
                </motion.li>
              ))}
            </motion.ul>
          </div>
        </section>

        {/* =====================================================
            WHO CAN USE
        ====================================================== */}
        <section className="bg-cyan-50 py-24 relative overflow-hidden">
          <div
            className="absolute inset-0 opacity-30 pointer-events-none"
            style={{
              backgroundImage:
                "radial-gradient(circle at 20% 20%, rgba(20,184,166,0.15), transparent 40%), radial-gradient(circle at 80% 80%, rgba(6,182,212,0.15), transparent 40%)",
            }}
          />

          <div className="relative max-w-6xl mx-auto px-6 text-center">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-teal-500">
              Who uses Doxzon
            </span>

            <h2 className="font-sora text-4xl md:text-5xl font-medium tracking-tight mt-4">
              Telemedicine Across the Healthcare Ecosystem
            </h2>

            <p className="text-gray-600 mt-6 max-w-3xl mx-auto leading-8">
              A solo practitioner and a multi-branch hospital chain use Doxzon
              differently, but the same system supports both.
            </p>

            <motion.div
              variants={stagger}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              className="grid grid-cols-2 md:grid-cols-3 gap-5 mt-14"
            >
              {users.map(({ icon: Icon, label }) => (
                <motion.div
                  key={label}
                  variants={fadeUp}
                  whileHover={{ y: -8, scale: 1.02 }}
                  className="group bg-white rounded-xl p-4 shadow-md border border-transparent hover:border-teal-200 hover:shadow-xl transition"
                >
                  <motion.div
                    whileHover={{ rotate: 8, scale: 1.1 }}
                    className="w-10 h-10 mx-auto rounded-lg bg-gradient-to-br from-teal-500 to-cyan-500 grid place-items-center"
                  >
                    <Icon size={18} className="text-white" />
                  </motion.div>

                  <h3 className="font-sora font-semibold text-sm text-slate-900 mt-2">
                    {label}
                  </h3>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* =====================================================
            SECURITY
        ====================================================== */}
        <section className="bg-slate-50 py-24">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
            className="max-w-4xl mx-auto px-6 text-center"
          >
            <motion.div
              whileHover={{ scale: 1.08, rotate: 5 }}
              className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-500 grid place-items-center shadow-lg"
            >
              <Lock size={24} className="text-white" />
            </motion.div>

            <span className="inline-block text-teal-500 uppercase tracking-widest font-semibold text-xs mt-6">
              Security &amp; privacy
            </span>

            <h2 className="font-sora text-4xl md:text-5xl font-medium tracking-tight mt-3">
              Healthcare Information Deserves Careful Protection
            </h2>

            <p className="text-gray-600 mt-6 leading-8">
              Doxzon is designed with healthcare data and patient privacy in
              mind. Access to healthcare information is handled carefully
              throughout the consultation and care journey.
            </p>
          </motion.div>
        </section>

        {/* =====================================================
            VIRTUAL CARE VS TELEMEDICINE
        ====================================================== */}
        <section className="py-24 bg-white">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center max-w-3xl mx-auto">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-teal-500">
                Terminology
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium tracking-tight mt-4">
                Telemedicine vs.
                <span className="text-teal-500"> Virtual Consultation</span>
              </h2>

              <p className="text-gray-600 mt-6 leading-8">
                Telemedicine and virtual consultation are related terms, but they describe
                different levels of the digital healthcare experience. Doxzon uses Telemedicine as the primary
                service terminology, while Virtual Consultation describes the specific consultation experience.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-5 mt-12">
              {[
                {
                  title: "Telemedicine",
                  desc: "Healthcare services delivered remotely through digital technologies, including appropriate clinical care.",
                },
                {
                  title: "Virtual Consultation",
                  desc: "A specific remote interaction between a patient and a healthcare professional.",
                },
              ].map((item) => (
                <motion.div
                  key={item.title}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={viewport}
                  whileHover={{ y: -6 }}
                  className="bg-slate-50 border border-slate-100 rounded-2xl p-5"
                >
                  <h3 className="font-sora text-base font-semibold text-slate-900">
                    {item.title}
                  </h3>
                  <p className="text-slate-600 text-xs leading-6 mt-2">
                    {item.desc}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* =====================================================
            FAQ
        ====================================================== */}
        <section className="py-24">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-teal-500">
                FAQ
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium tracking-tight mt-4">
                Frequently Asked Questions
              </h2>
            </div>

            <motion.div
              variants={stagger}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              className="mt-12 space-y-4"
            >
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  variants={fadeUp}
                  whileHover={{ y: -5 }}
                  className="bg-white border border-slate-100 rounded-xl p-4 hover:border-teal-200 hover:shadow-lg transition"
                >
                  <div className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 text-white grid place-items-center text-xs font-bold">
                      0{index + 1}
                    </span>

                    <div>
                      <h3 className="font-sora text-base md:text-lg font-semibold text-slate-900">
                        {faq.q}
                      </h3>

                      <p className="text-gray-600 text-sm mt-2 leading-6">
                        {faq.a}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* =====================================================
            CTA
        ====================================================== */}
        <section className="py-24">
          <div className="max-w-6xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="relative overflow-hidden rounded-[32px] bg-gradient-to-br from-teal-500 via-cyan-500 to-teal-600 p-8 md:p-12"
            >
              <motion.div
                className="absolute -top-32 -right-20 w-96 h-96 rounded-full border border-white/20"
                animate={{
                  rotate: 360,
                  scale: [1, 1.1, 1],
                }}
                transition={{
                  rotate: {
                    duration: 20,
                    repeat: Infinity,
                    ease: "linear",
                  },
                  scale: {
                    duration: 5,
                    repeat: Infinity,
                  },
                }}
              />

              <motion.div
                className="absolute -bottom-40 -left-20 w-96 h-96 rounded-full bg-white/10 blur-2xl"
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 6, repeat: Infinity }}
              />

              <div className="relative grid md:grid-cols-2 gap-12 items-center">
                <div>
                  <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-white/80">
                    <Sparkles size={14} />
                    Connected healthcare
                  </span>

                  <h2 className="font-sora text-4xl md:text-5xl font-medium text-white leading-tight mt-5">
                    Make remote care
                    <br />
                    feel effortless.
                  </h2>
                </div>

                <div>
                  <p className="text-white/80 text-lg leading-8">
                    Give patients an easier way to connect with healthcare
                    professionals while keeping your consultation workflow
                    connected.
                  </p>

                  <div className="flex flex-wrap gap-3 mt-7">
                    <Link to="/consultdoctor">
                      <motion.button
                        whileHover={{ scale: 1.04 }}
                        whileTap={{ scale: 0.97 }}
                        className="inline-flex items-center gap-2 bg-white text-slate-950 px-7 py-3.5 rounded-full font-semibold"
                      >
                        Find a Healthcare Professional
                        <ArrowRight size={17} />
                      </motion.button>
                    </Link>
                    <Link to="/contact">
                      <motion.button
                        whileHover={{
                          backgroundColor: "rgba(255,255,255,0.15)",
                        }}
                        className="border border-white/50 text-white px-7 py-3.5 rounded-full font-semibold"
                      >
                        Contact us
                      </motion.button>
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>

          </div>
        </section>
      </div>
    </>
  );
}